'use client';

import { useJsApiLoader } from '@react-google-maps/api';
import { useCallback } from 'react';

import { getGoogleKeyApi } from './google-enviroment';

import { formatGoogleGeocoderToPlace } from '@/app/(main)/(pages)/(home)/_components/location/_consts/format-google-geocoder-to-place.const';
import { AutocompletePrediction, Place } from '@/types/google-maps.types';

let sessionToken: google.maps.places.AutocompleteSessionToken | undefined;

export function useGoogleMaps() {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'medprev-google-maps',
    googleMapsApiKey: getGoogleKeyApi() ?? '',
    language: 'pt-BR',
    region: 'BR',
  });

  const getSessionToken = useCallback(async (): Promise<google.maps.places.AutocompleteSessionToken> => {
    if (!sessionToken) {
      const { AutocompleteSessionToken } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;

      sessionToken = new AutocompleteSessionToken();
    }

    return sessionToken;
  }, []);

  const resetSessionToken = useCallback((): void => {
    sessionToken = undefined;
  }, []);

  const getGoogleMapsPredictions = useCallback(async (search: string): Promise<AutocompletePrediction[]> => {
    const currentSessionToken = await getSessionToken();

    const { AutocompleteService } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;

    const autocompleteService = new AutocompleteService();

    const { predictions } = await autocompleteService.getPlacePredictions({
      input: search,
      sessionToken: currentSessionToken,
      types: ['geocode'],
      language: 'pt-BR',
      componentRestrictions: { country: 'br' },
    });

    return (predictions ?? []).map((prediction) => ({
      description: prediction.description,
      placeId: prediction.place_id,
      types: prediction.types || [],
      formattedAddress: prediction.description,
    }));
  }, [getSessionToken]);

  const getGoogleMapsPlaceDetailsWithGeocoder = useCallback(async (placeId: string): Promise<Place> => {
    if (!placeId) {
      throw new Error('Geocoder called without a placeId');
    }

    const geocoderService = new google.maps.Geocoder();

    const matchedPlace = await geocoderService.geocode({ placeId });

    const firstResult = matchedPlace.results[0];

    if (!firstResult) {
      throw new Error(`Geocoder returned no results for placeId ${placeId}`);
    }

    return formatGoogleGeocoderToPlace([firstResult]);
  }, []);

  const getGoogleMapsPlaceByCoordinates = useCallback(async (): Promise<Place> => {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition((position) => {
        resolve(position);
      }, reject);
    });

    const geocoderService = new google.maps.Geocoder();

    const geocoderResponse = await geocoderService.geocode({
      location: {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      },
    });

    return formatGoogleGeocoderToPlace(geocoderResponse.results);
  }, []);

  const getGoogleMapsPlaceByAddress = useCallback(async (address: string): Promise<Place | null> => {
    if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) {
      return null;
    }

    const geocoderService = new google.maps.Geocoder();

    const geocoderResponse = await geocoderService.geocode({ address, fulfillOnZeroResults: true });

    if (!geocoderResponse.results?.length) {
      return null;
    }

    return formatGoogleGeocoderToPlace(geocoderResponse.results);
  }, []);

  const getGoogleMapsPlaceByCityAndState = useCallback(async (city: string, state: string): Promise<Place | null> => {
    return getGoogleMapsPlaceByAddress(`${city}, ${state}, Brasil`);
  }, [getGoogleMapsPlaceByAddress]);

  return {
    isLoaded,
    loadError,
    getGoogleMapsPredictions,
    getGoogleMapsPlaceDetailsWithGeocoder,
    getGoogleMapsPlaceByCoordinates,
    getGoogleMapsPlaceByAddress,
    getGoogleMapsPlaceByCityAndState,
    resetSessionToken,
  };
}
